import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import { Loading } from '@folio/stripes/components';
import useReport from '../hooks/useReport';

/**
 * Build cataloging form_state from queryMetadata when builderState is empty
 */
function buildCatalogingStateFromQuery(queryMetadata) {
  if (!queryMetadata) return {};
  const { measures = [], dimensions = [], filters = [] } = queryMetadata;

  // Pass as simple string arrays - mapFieldArray in catalogingReportUtils will convert them
  const instanceFields = dimensions.filter(d =>
    d.startsWith('cataloging_view.') &&
    !d.includes('holdings') &&
    !d.includes('item_')
  );

  const holdingsFields = dimensions.filter(d =>
    d.startsWith('cataloging_view.') &&
    (d.includes('holdings') || d.includes('call_number') || d.includes('location'))
  );

  const itemFields = dimensions.filter(d =>
    d.startsWith('cataloging_view.') &&
    d.includes('item_')
  );

  return {
    instanceFields,
    holdingsFields,
    itemFields,
    measures,
    instanceFilterOperator: 'and',
    holdingsFilterOperator: 'and',
    itemFilterOperator: 'and',
    measuresFilterOperator: 'and',
  };
}

/**
 * Build circulation form_state from queryMetadata
 */
function buildCirculationStateFromQuery(queryMetadata) {
  if (!queryMetadata) return {};
  const { measures = [], dimensions = [] } = queryMetadata;

  const loanFields = dimensions.filter(d =>
    d.includes('loan_') || d.includes('.loan_') ||
    ['circulation_view.loan_status', 'circulation_view.renewal_count', 'circulation_view.due_date', 'circulation_view.return_date'].includes(d)
  );

  const patronFields = dimensions.filter(d =>
    d.includes('patron') || d.includes('borrower')
  );

  const itemFields = dimensions.filter(d =>
    d.includes('item') || d.includes('title') || d.includes('material_type') || d.includes('call_number')
  );

  return {
    loanFields,
    patronFields,
    itemFields,
    measures,
    loanFilterOperator: 'and',
    patronFilterOperator: 'and',
    itemFilterOperator: 'and',
  };
}

function EditQueryRoute() {
  const { reportId } = useParams();
  const history = useHistory();
  const { reports: report, isLoading } = useReport(reportId);

  // Detect if this is a run action (hide builder pane)
  const searchParams = new URLSearchParams(history.location?.search || window.location.search);
  const isRunMode = searchParams.get('run') === 'true';
  // Preserve the list filters to restore them when closing the builder
  const returnSearch = history.location?.state?.returnSearch || '';

  useEffect(() => {
    if (isLoading || !report) return;

    const builderType = report.builderType || 'advanced';
    const builderState = report.builderState || {};
    const queryMetadata = report.queryMetadata || {};

    // Check if builderState has actual content
    const hasBuilderState = Object.keys(builderState).length > 0 &&
      (builderState.instanceFields || builderState.loanFields ||
       builderState.measures || builderState.selectedFields);

    const routes = {
      cataloging: '/reports/cataloging-reports',
      circulation: '/reports/circulation-reports',
      productivity: '/reports/productivity-reports',
      advanced: '/reports/query-builder',
    };

    const targetRoute = routes[builderType] || routes.advanced;

    let stateParam = '';

    if (hasBuilderState) {
      stateParam = typeof builderState === 'string'
        ? builderState
        : JSON.stringify(builderState);
    } else {
      let reconstructed = {};
      if (builderType === 'cataloging') {
        reconstructed = buildCatalogingStateFromQuery(queryMetadata);
      } else if (builderType === 'circulation') {
        reconstructed = buildCirculationStateFromQuery(queryMetadata);
      } else if (builderType === 'productivity' || builderType === 'advanced') {
        const queryString = report.queryString || queryMetadata;
        const cleanQuery = typeof queryString === 'object'
          ? JSON.stringify(queryString)
          : queryString;
        history.push({
          pathname: targetRoute,
          search: `?q=${encodeURIComponent(cleanQuery)}${isRunMode ? '&hide_builder=true' : ''}&report_id=${report.id}${isRunMode ? '&run=true' : ''}`,
          state: {
            reportId: report.id,
            reportName: report.name,
            reportDesc: report.desc,
            isRunMode,
            isEditMode: !isRunMode,
            returnSearch,
            defaultDisplayMethod: report.visualization?.defaultDisplayMethod || 'table',
            displayMethods: report.visualization?.displayMethods || [],
          },
        });
        return;
      }
      stateParam = JSON.stringify(reconstructed);
    }

    history.push({
      pathname: targetRoute,
      search: `?form_state=${encodeURIComponent(stateParam)}${isRunMode ? '&hide_builder=true' : ''}&report_id=${report.id}${isRunMode ? '&run=true' : ''}`,
      state: {
        reportId: report.id,
        reportName: report.name,
        reportDesc: report.desc,
        isRunMode,
        isEditMode: !isRunMode,
        returnSearch,
        defaultDisplayMethod: report.visualization?.defaultDisplayMethod || 'table',
        displayMethods: report.visualization?.displayMethods || [],
      },
    });
  }, [report, isLoading]);

  return <Loading size="xlarge" />;
}

EditQueryRoute.propTypes = {};

export default EditQueryRoute;
